/*
 * Purpose: Resolve plan → AutoBlocks native connectors (no third-party deps)
 * Last modified: 2026-06-25 | Agent
 * Completeness: 88
 */
import { useConnectorLibraryStore } from './store';
import type { ResolvedConnector } from './types';
import type { ConnectorFactoryResult } from './connectorFactory';
import type { OutcomePlan } from '@/services/autoblocks/types';
import { hasResolvedSecret } from '@/lib/resolveSecret';
import { getGmailAccessToken } from '@/services/connectors/gmailOAuth';
import { hasAnyMcpConnected, hasMcpServerConfigured, isMcpServerConnected } from '@/services/connectors/mcp/mcpConnection';
import { mcpPresetIdFromConnectorId } from '@/services/connectors/mcp/mcpPresets';
import { CAREEROS_CONNECTOR_ID, CAREEROS_MCP_SERVER_ID } from '@/seeds/careerosPrivateConnector';

function resolveStatus(
  connectorId: string,
  plan: OutcomePlan
): ResolvedConnector['status'] {
  if (connectorId === 'ab.gmail') {
    if (getGmailAccessToken()) return 'ready';
    if (plan.gmailInboxId) return 'ready';
    return 'needs_connect';
  }
  if (connectorId === 'ab.openai') {
    return hasResolvedSecret('OPENAI_API_KEY') ? 'ready' : 'needs_credentials';
  }
  if (connectorId === 'ab.linkedin') {
    return isLinkedInConnected() ? 'ready' : 'needs_connect';
  }
  if (connectorId === 'ab.mcp') {
    return hasAnyMcpConnected() ? 'ready' : 'needs_connect';
  }
  const presetId = mcpPresetIdFromConnectorId(connectorId);
  if (presetId) {
    return isMcpServerConnected(presetId) ? 'ready' : 'needs_connect';
  }
  if (connectorId === CAREEROS_CONNECTOR_ID) {
    return isMcpServerConnected(CAREEROS_MCP_SERVER_ID) ? 'ready' : 'needs_connect';
  }
  return 'ready';
}

export function resolveConnectorsForPlan(
  plan: OutcomePlan,
  factory: ConnectorFactoryResult
): ResolvedConnector[] {
  const lib = useConnectorLibraryStore.getState();

  return factory.connectorIds.map((connectorId) => {
    const def =
      lib.getById(connectorId) ??
      factory.builtConnectors.find((c) => c.id === connectorId);

    if (!def) {
      return {
        connectorId,
        name: connectorId,
        purpose: 'Connector',
        status: 'scaffold',
        setupSteps: ['Register connector in AutoBlocks library.'],
        capabilitiesUsed: [],
      };
    }

    const isNew = factory.builtConnectors.some((c) => c.id === connectorId);
    const status = resolveStatus(connectorId, plan);

    return {
      connectorId: def.id,
      name: def.name,
      purpose: def.description,
      status,
      setupSteps: def.setupSteps,
      capabilitiesUsed: def.capabilities.map((c) => c.id),
      isNewlyBuilt: isNew,
    };
  });
}
